import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { Calendar as CalendarIcon, Clock, MapPin, Coffee, CheckCircle, XCircle } from 'lucide-react';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subDays } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UserTimesheet() {
  const { token } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    start: startOfWeek(new Date(), { weekStartsOn: 1 }),
    end: endOfWeek(new Date(), { weekStartsOn: 1 })
  });
  const [calendarOpen, setCalendarOpen] = useState(false);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/time/entries`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          start_date: format(dateRange.start, 'yyyy-MM-dd'),
          end_date: format(dateRange.end, 'yyyy-MM-dd')
        }
      });
      setEntries(response.data);
    } catch (error) {
      toast.error('Failed to load timesheet entries');
    } finally {
      setLoading(false);
    }
  }, [token, dateRange]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const setQuickRange = (range) => {
    const today = new Date();
    switch (range) {
      case 'week':
        setDateRange({
          start: startOfWeek(today, { weekStartsOn: 1 }),
          end: endOfWeek(today, { weekStartsOn: 1 })
        });
        break;
      case 'month':
        setDateRange({
          start: startOfMonth(today),
          end: endOfMonth(today)
        });
        break;
      case 'last7':
        setDateRange({
          start: subDays(today, 7),
          end: today
        });
        break;
      case 'last30':
        setDateRange({
          start: subDays(today, 30),
          end: today
        });
        break;
      default:
        break;
    }
  };

  const formatDuration = (hours) => {
    if (!hours && hours !== 0) return '--:--';
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  const totalHours = entries.reduce((sum, entry) => sum + (entry.total_hours || 0), 0);

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold">My Timesheet</h1>
            <p className="text-muted-foreground">Track your work hours and breaks</p>
          </div>

          {/* Date Range Picker */}
          <div className="flex flex-wrap items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setQuickRange('week')}
              data-testid="this-week-btn"
            >
              This Week
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setQuickRange('month')}
              data-testid="this-month-btn"
            >
              This Month
            </Button>
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" data-testid="date-range-picker">
                  <CalendarIcon className="h-4 w-4 mr-2" />
                  {format(dateRange.start, 'MMM d')} - {format(dateRange.end, 'MMM d')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                  mode="range"
                  selected={{ from: dateRange.start, to: dateRange.end }}
                  onSelect={(range) => {
                    if (range?.from && range?.to) {
                      setDateRange({ start: range.from, end: range.to });
                      setCalendarOpen(false);
                    }
                  }}
                  numberOfMonths={2}
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Summary Card */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card data-testid="total-entries-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Entries</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{entries.length}</div>
            </CardContent>
          </Card>
          <Card data-testid="total-hours-summary-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Hours</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatDuration(totalHours)}</div>
            </CardContent>
          </Card>
          <Card data-testid="approved-entries-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Approved</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-emerald-600">
                {entries.filter(e => e.approved).length}
              </div>
            </CardContent>
          </Card>
          <Card data-testid="pending-entries-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Pending</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-amber-600">
                {entries.filter(e => !e.approved && e.status === 'completed').length}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Entries Table */}
        <Card>
          <CardHeader>
            <CardTitle>Time Entries</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="h-8 w-8 spinner border-primary" />
              </div>
            ) : entries.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No entries found for this period</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Project</TableHead>
                      <TableHead>Clock In</TableHead>
                      <TableHead>Clock Out</TableHead>
                      <TableHead>Breaks</TableHead>
                      <TableHead>Total</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {entries.map((entry) => (
                      <TableRow key={entry.id} data-testid={`entry-row-${entry.id}`}>
                        <TableCell className="font-medium">
                          {format(new Date(entry.date + 'T12:00:00'), 'EEE, MMM d')}
                        </TableCell>
                        <TableCell>
                          {entry.project_name ? (
                            <div className="flex items-center gap-2">
                              <div 
                                className="h-3 w-3 rounded-full flex-shrink-0" 
                                style={{ backgroundColor: entry.project_color || '#6366f1' }}
                              />
                              <div>
                                <span className="font-medium">{entry.project_name}</span>
                                {entry.task_description && (
                                  <p className="text-xs text-muted-foreground truncate max-w-32">
                                    {entry.task_description}
                                  </p>
                                )}
                              </div>
                            </div>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {format(new Date(entry.clock_in), 'h:mm a')}
                        </TableCell>
                        <TableCell>
                          {entry.clock_out 
                            ? format(new Date(entry.clock_out), 'h:mm a')
                            : <Badge variant="outline">Active</Badge>
                          }
                        </TableCell>
                        <TableCell>
                          {entry.breaks?.length > 0 ? (
                            <div className="flex items-center gap-1">
                              <Coffee className="h-4 w-4 text-amber-500" />
                              <span>{entry.breaks.length}</span>
                            </div>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="font-medium">
                          {formatDuration(entry.total_hours)}
                        </TableCell>
                        <TableCell>
                          {entry.status === 'completed' ? (
                            entry.approved ? (
                              <Badge className="badge-approved">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Approved
                              </Badge>
                            ) : (
                              <Badge className="badge-pending">
                                <Clock className="h-3 w-3 mr-1" />
                                Pending
                              </Badge>
                            )
                          ) : (
                            <Badge variant="outline">
                              {entry.status === 'on_break' ? 'On Break' : 'Active'}
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
